const organizationUnitIdsQuery = (organizationUnitIds: string[]): string =>
  organizationUnitIds.map((organizationUnitId) => `organizationUnitId=${encodeURIComponent(organizationUnitId)}`).join('&');

/**
 * Base path of a tenant-scoped Media Library resource. Segments are interpolated verbatim, so callers pass
 * already URL-encoded ids - that also lets the same builder produce the `{tenantId}` / `{fileId}` placeholder
 * form used by the `endpoint-info` annotations in templates.
 */
const mediaLibraryTenantResource = (tenantId: string, collection: string, id?: string): string => {
  const basePath = `medialibrary/v5/tenants/${tenantId}/${collection}`;
  return id ? `${basePath}/${id}` : basePath;
};

const teamIdsQuery = (teamIds: string[]): string => teamIds.map((teamId) => `teamId=${encodeURIComponent(teamId)}`).join('&');

export const GlobalApiRoutes = {
  CallHandler: {
    getSettings: (): string => 'callhandler/v2/settings',
  },

  MediaLibrary: {
    getSettings: (): string => 'medialibrary/v3/settings',
    getCallRecordCounts: (tenantId: string, teamIds: string[], isProcessed?: boolean, currentlyHandled?: boolean): string => {
      let url = `${mediaLibraryTenantResource(tenantId, 'CallRecords')}/count/batch?${teamIdsQuery(teamIds)}`;
      if (isProcessed !== undefined && isProcessed !== null) {
        url += `&isProcessed=${encodeURIComponent(String(isProcessed))}`;
      }
      if (currentlyHandled !== undefined && currentlyHandled !== null) {
        url += `&currentlyHandled=${encodeURIComponent(String(currentlyHandled))}`;
      }
      return url;
    },
    getCallRecordCountsByDay: (tenantId: string, teamIds: string[], from: string): string =>
      `${mediaLibraryTenantResource(tenantId, 'CallRecords')}/countByDay/batch?${teamIdsQuery(teamIds)}` +
      `&from=${encodeURIComponent(from)}`,
    getAverageProcessingDurations: (tenantId: string, teamIds: string[], fromDate?: string, toDate?: string): string => {
      let url = `${mediaLibraryTenantResource(tenantId, 'CallRecords')}/average-processing-duration/batch?${teamIdsQuery(teamIds)}`;
      if (fromDate) {
        url += `&fromDate=${encodeURIComponent(fromDate)}`;
      }
      if (toDate) {
        url += `&toDate=${encodeURIComponent(toDate)}`;
      }
      return url;
    },
    tenantResource: mediaLibraryTenantResource,
    getAudioFileMetadata: (tenantId: string, fileId: string): string =>
      `${mediaLibraryTenantResource(tenantId, 'audiofiles', fileId)}/metadata`,
  },

  PermissionManager: {
    getUserPermissions: (userId: string): string => `permissionmanager/v1/Permissions/${userId}/model`,
    getGlobalPermissions: (tenantId: string, userId: string): string =>
      `permissionmanager/v1/Permissions/${tenantId}/${userId}/global-model`,
    getGlobalPermissionsForProvisioning: (tenantId: string, userId: string): string =>
      `permissionmanager/v1/Permissions/${tenantId}/${userId}/global-model/provisioning`,
    getGlobalAdminPermissions: (tenantId: string, userId: string, requestLocation: boolean): string =>
      `permissionmanager/v1/Permissions/${tenantId}/${userId}/global-model/admin?requestLocation=${requestLocation}`,
    getTenantPermissions: (tenantId: string, userId: string): string =>
      `permissionmanager/v1/Permissions/${tenantId}/${userId}/combined-model`,
    getMyHash: (): string => `permissionmanager/v1/my-hash`,
  },

  PresenceState: {
    getServiceAccounts: (): string => 'presence/v1/ServiceAccounts',
    getGuestServiceAccounts: (): string => 'presence/v1/ServiceAccounts/guest-tracking',
    updateServiceAccounts: (id: string): string => `presence/v1/ServiceAccounts/${id}`,
    getServiceAccountStats: (): string => 'presence/v1/ServiceAccounts/stats',
    getPollingInterval: (): string => 'presence/v1/Settings',
    getTenantPresenceSettings: (tenantId: string): string => `presence/v1/Tenants/${tenantId}/settings`,
    checkPresenceWithGuestAccount: (tenantId: string, id: 'primary' | 'secondary'): string =>
      `presence/v1/Tenants/${tenantId}/check-presence-${id}`,
    checkPresenceByApplication: (tenantId: string): string => `presence/v1/Tenants/${tenantId}/check-presence-by-application`,
    checkPresenceByTenant: (tenantId: string): string => `acs/tenants/${encodeURIComponent(tenantId)}/presences?api-version=2025-07-21`,
  },

  FeatureFlipper: {
    get: (): string => 'feature-flipping/v1/Feature',
    getTeamsEnterpriseStateByTenantId: (tenantId: string): string => `facade/v1/admin-portal/tenants/${tenantId}/teams`,
  },

  LicenseManager: {
    getLicenseStateInfo: (tenantId: string): string => `license-manager/v1/tenants/${tenantId}/license-state-info`,

    getServiceLicense: (teamId: string): string => `license-manager/v1/Teams/${teamId}/license`,
    getServiceLicensesInfo: (teamId: string): string => `license-manager/v1/Teams/${teamId}/licenses-info`,
    getServiceLicensesUsageInfo: (tenantId: string, userId: string): string =>
      `license-manager/v1/Tenants/${tenantId}/users/${userId}/services-licenses-usage`,
    getServiceLicenseAddons: (teamId: string): string => `license-manager/v1/Teams/${teamId}/license-addons`,
    getServiceLicensesAddonsInfo: (teamId: string): string => `license-manager/v1/Teams/${teamId}/licenses-info-addons`,
    setServiceLicense: (teamId: string): string => `license-manager/v1/Teams/${teamId}/license`,
    setServiceLicenseAddons: (teamId: string): string => `license-manager/v1/Teams/${teamId}/license-addons`,

    getUserLicenses: (userId: string): string => `license-manager/v1/users/${userId}/licenses`,
    getUserLicensesInfo: (userId: string): string => `license-manager/v1/users/${userId}/licenses-info`,
    getUserLicensesUsageInfo: (tenantId: string, userId: string): string =>
      `license-manager/v1/Tenants/${tenantId}/users/${userId}/users-licenses-usage`,
    setUserLicenses: (organizationUnitId: string, userId: string): string =>
      `license-manager/v1/users/${userId}/licenses?organizationUnitId=${organizationUnitId}`,
  },

  TeamManager: {
    getUserAssignmentType: (teamId: string): string => `teammanager/v1/teams/${teamId}/user-assignment-type`,
    getDistributionPrioritySettings: (teamId: string): string => `teammanager/v1/Teams/${teamId}/distribution-priority-settings`,
    setDistributionPrioritySettings: (teamId: string): string => `teammanager/v1/Teams/${teamId}/distribution-priority-settings`,
    setTeamOwnerActiveState: (teamId: string, memberId: string): string => `teammanager/v1/teams/${teamId}/members/${memberId}/enability`,
    setMyActiveState: (teamId: string, memberId: string): string =>
      `teammanager/v1/teams/${teamId}/members/${memberId}/enability-with-check`,
  },

  Facade: {
    getUserAssignmentSettings: (teamId: string): string => `facade/v1/admin-portal/teams/${teamId}/user-assignment-settings`,
    setUserAssignmentSettings: (tenantId: string, teamId: string): string =>
      `facade/v1/admin-portal/tenants/${tenantId}/teams/${teamId}/user-assignment-settings`,
    searchUsersForDirectCall: (tenantId: string): string =>
      `facade/v1/admin-portal/tenants/${encodeURIComponent(tenantId)}/users/for-direct-call/search`,
    IsValidRegexp(regexp: string): string {
      return `facade/v1/common-portal/validate-regex?pattern=${regexp}`;
    },
  },

  Tenant: {
    getTenantInfo: (tenantId: string): string => `organization-unit-manager/v2/Tenants/${tenantId}/tenant-info`,
  },

  MsGraphApiAdapter: {
    getUserByUpn(tenantId: string, upn: string): string {
      return `ms-graph-api-adapter/v1/users/${encodeURIComponent(upn)}?tenantId=${encodeURIComponent(tenantId)}&select=id`;
    },
  },

  AcsDriver: {
    getGuestServiceAccounts: (): string => 'acs-driver/v1/accountConfiguration',
    globalSettings: (): string => 'acs-driver/v1/global-settings',
    updateServiceAccounts: (): string => `acs-driver/v1/accountConfiguration`,
    testChatGraphThread(tenantId: string, accountId: string, testUpn: string): string {
      return `acs-driver/v1/AccountConfiguration/tenants/${tenantId}/check?accountId=${accountId}&upn=${testUpn}`;
    },
  },

  Locations: {
    getLocationsByTenantId(tenantId: string): string {
      return `tenant-location/v1/locations/${tenantId}/customer`;
    },
  },

  WebRequestActivity: {
    getAll: (tenantId: string): string => `public-api-next/tenants/${tenantId}/web-requests`,
    getById: (tenantId: string, id: string, organizationUnitId: string): string =>
      `public-api-next/tenants/${tenantId}/web-requests/${id}?organizationUnitId=${organizationUnitId}`,
    create: (tenantId: string, organizationUnitId: string): string =>
      `public-api-next/tenants/${tenantId}/web-requests?organizationUnitId=${organizationUnitId}`,
    edit: (tenantId: string, id: string, organizationUnitIds: string[]): string =>
      `public-api-next/tenants/${tenantId}/web-requests/${id}?${organizationUnitIdsQuery(organizationUnitIds)}`,
    delete: (tenantId: string, id: string, organizationUnitId: string): string =>
      `public-api-next/tenants/${tenantId}/web-requests/${id}?organizationUnitId=${organizationUnitId}`,
    test: (tenantId: string): string => `public-api-next/tenants/${tenantId}/web-requests/test`,
  },

  WebRequestAuthentication: {
    getAll: (tenantId: string): string => `public-api-next/tenants/${tenantId}/authentications`,
    getForMcpServers: (tenantId: string): string => `public-api-next/tenants/${tenantId}/authentications/for-mcp-servers`,
    getById: (tenantId: string, id: string, organizationUnitId: string): string =>
      `public-api-next/tenants/${tenantId}/authentications/${id}?organizationUnitId=${organizationUnitId}`,
    create: (tenantId: string, organizationUnitId: string): string =>
      `public-api-next/tenants/${tenantId}/authentications?organizationUnitId=${organizationUnitId}`,
    edit: (tenantId: string, id: string, organizationUnitIds: string[]): string =>
      `public-api-next/tenants/${tenantId}/authentications/${id}?${organizationUnitIdsQuery(organizationUnitIds)}`,
    delete: (tenantId: string, id: string, organizationUnitId: string): string =>
      `public-api-next/tenants/${tenantId}/authentications/${id}?organizationUnitId=${organizationUnitId}`,
  },
};
